import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Filter, Clock } from 'lucide-react';
import {
  AdminClientApi,
  ClientUserInterface,
  ClientBelief,
  ClientThoughtWithAuthor,
  ClientFeed,
} from '../../Admin-ClientLibrary';
import BeliefsListView from '../../components/BeliefsListView';
import { getApiUrl } from '../../config/api';
import { useRecentEntities } from '../../contexts/RecentEntitiesContext';
import { RecentEntity } from '../../types/RecentEntities';
import './Beliefs.css';

// Initialize the Admin Client Library
const getToken = (): string => {
  return localStorage.getItem('authToken') || '';
};

const adminApi = new AdminClientApi(getApiUrl(), getToken);

const Beliefs: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { recentEntities } = useRecentEntities();

  const [showFilters, setShowFilters] = useState<boolean>(false);
  const [openRecentDropdown, setOpenRecentDropdown] = useState<string | null>(null);

  // Initialize filters from URL parameters
  const [filters, setFilters] = useState({
    position: searchParams.get('position') || '',
    userId: searchParams.get('userId') || '',
    goeUserId: searchParams.get('goeUserId') || '',
    loeUserId: searchParams.get('loeUserId') || '',
    thoughtId: searchParams.get('thoughtId') || '',
    createdAfter: searchParams.get('createdAfter') || '',
    createdBefore: searchParams.get('createdBefore') || '',
    supportingBeliefsFor: searchParams.get('supportingBeliefsFor') || '',
  });
  const [appliedFilters, setAppliedFilters] = useState({
    position: searchParams.get('position') || '',
    userId: searchParams.get('userId') || '',
    goeUserId: searchParams.get('goeUserId') || '',
    loeUserId: searchParams.get('loeUserId') || '',
    thoughtId: searchParams.get('thoughtId') || '',
    createdAfter: searchParams.get('createdAfter') || '',
    createdBefore: searchParams.get('createdBefore') || '',
    supportingBeliefsFor: searchParams.get('supportingBeliefsFor') || '',
  });

  // Close recent dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (!target.closest('.recent-dropdown-container')) {
        setOpenRecentDropdown(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Function to update URL parameters
  const updateUrlParams = (params: Record<string, string | number | undefined>): void => {
    const newParams = new URLSearchParams();

    // Add all current search params first
    searchParams.forEach((value, key) => {
      newParams.set(key, value);
    });

    // Update with new params
    Object.entries(params).forEach(([key, value]) => {
      if (value && value !== '') {
        newParams.set(key, value.toString());
      } else {
        newParams.delete(key);
      }
    });

    setSearchParams(newParams);
  };

  const handleFilterChange = (filterName: string, value: string): void => {
    setFilters((prev) => ({ ...prev, [filterName]: value }));
  };

  const handleQuickUserFilter = (goeValue: string, loeValue: string): void => {
    setFilters((prev) => ({ ...prev, goeUserId: goeValue, loeUserId: loeValue }));
  };

  // Recent entities helpers
  const getFilteredRecentEntities = (type: 'user' | 'belief' | 'thought'): RecentEntity[] => {
    return recentEntities.filter((entity) => entity.type === type);
  };

  const handleRecentEntityClick = (entity: RecentEntity, filterField: string): void => {
    handleFilterChange(filterField, entity.id);
    setOpenRecentDropdown(null);
  };

  const toggleRecentDropdown = (dropdownType: string): void => {
    setOpenRecentDropdown(openRecentDropdown === dropdownType ? null : dropdownType);
  };

  const getEntityDisplayText = (entity: RecentEntity): string => {
    switch (entity.type) {
      case 'user':
        const user = entity.data as ClientUserInterface;
        return `${user.name || `User ${entity.id}`}`;
      case 'belief':
        const belief = entity.data as ClientBelief;
        return `Belief ${entity.id} (${belief.position?.toUpperCase() || 'UNKNOWN'})`;
      case 'thought':
        const thought = entity.data as ClientThoughtWithAuthor;
        const text = thought.text || '';
        return text.length > 40 ? `${text.substring(0, 40)}...` : text;
      case 'feed':
        const feed = entity.data as ClientFeed;
        return feed.title || `Feed ${entity.id}`;
      default:
        return `Entity ${entity.id}`;
    }
  };

  const applyFilters = (): void => {
    setAppliedFilters(filters);
    updateUrlParams(filters);
    setShowFilters(false);
  };

  const clearFilters = (): void => {
    const emptyFilters = {
      position: '',
      userId: '',
      goeUserId: '',
      loeUserId: '',
      thoughtId: '',
      createdAfter: '',
      createdBefore: '',
      supportingBeliefsFor: '',
    };
    setFilters(emptyFilters);
    setAppliedFilters(emptyFilters);
    updateUrlParams(emptyFilters);
  };

  const getFilterSummary = (): string => {
    const activeFilters = [];
    if (appliedFilters.position) activeFilters.push(`Position: ${appliedFilters.position}`);
    if (appliedFilters.userId) activeFilters.push(`User ID: ${appliedFilters.userId}`);
    if (appliedFilters.goeUserId) activeFilters.push(`User ID >= ${appliedFilters.goeUserId}`);
    if (appliedFilters.loeUserId) activeFilters.push(`User ID <= ${appliedFilters.loeUserId}`);
    if (appliedFilters.thoughtId) activeFilters.push(`On thought: ${appliedFilters.thoughtId}`);
    if (appliedFilters.createdAfter) activeFilters.push(`After: ${appliedFilters.createdAfter}`);
    if (appliedFilters.createdBefore) activeFilters.push(`Before: ${appliedFilters.createdBefore}`);
    if (appliedFilters.supportingBeliefsFor) {
      activeFilters.push(`Supporting: ${appliedFilters.supportingBeliefsFor}`);
    }

    if (activeFilters.length === 0) {
      return 'No filters applied';
    }

    if (activeFilters.length <= 2) {
      return activeFilters.join(', ');
    }

    return `${activeFilters.slice(0, 2).join(', ')} and ${activeFilters.length - 2} more`;
  };

  // RecentDropdown component
  const RecentDropdown: React.FC<{
    type: 'user' | 'belief' | 'thought';
    dropdownKey: string;
    filterField: string;
    placeholder: string;
  }> = ({ type, dropdownKey, filterField, placeholder }) => {
    const filteredEntities = getFilteredRecentEntities(type);
    const isOpen = openRecentDropdown === dropdownKey;

    return (
      <div className="recent-dropdown-container">
        <button
          type="button"
          className="recent-button"
          onClick={() => toggleRecentDropdown(dropdownKey)}
          disabled={filteredEntities.length === 0}
        >
          <Clock size={14} />
          Recent
        </button>
        {isOpen && filteredEntities.length > 0 && (
          <div className="recent-dropdown">
            <div className="recent-dropdown-header">
              Recent {type === 'user' ? 'Users' : type === 'belief' ? 'Beliefs' : 'Thoughts'}
            </div>
            <ul className="recent-dropdown-list">
              {filteredEntities.slice(0, 5).map((entity) => (
                <li
                  key={`${entity.type}-${entity.id}`}
                  className="recent-dropdown-item"
                  onClick={() => handleRecentEntityClick(entity, filterField)}
                >
                  <span className="recent-entity-id">ID: {entity.id}</span>
                  <span className="recent-entity-text">{getEntityDisplayText(entity)}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    );
  };

  // Convert filters to the format expected by BeliefsListView
  const listViewFilters = {
    position: appliedFilters.position || undefined,
    userId: appliedFilters.userId ? parseInt(appliedFilters.userId) : undefined,
    goeUserId: appliedFilters.goeUserId ? parseInt(appliedFilters.goeUserId) : undefined,
    loeUserId: appliedFilters.loeUserId ? parseInt(appliedFilters.loeUserId) : undefined,
    thoughtId: appliedFilters.thoughtId ? parseInt(appliedFilters.thoughtId) : undefined,
    createdAfter: appliedFilters.createdAfter || undefined,
    createdBefore: appliedFilters.createdBefore || undefined,
    supportingBeliefsFor: appliedFilters.supportingBeliefsFor
      ? parseInt(appliedFilters.supportingBeliefsFor)
      : undefined,
  };

  return (
    <div className="beliefs-page">
      <div className="beliefs-header">
        <div className="header-top">
          <h1>Beliefs</h1>
        </div>

        <div className="beliefs-filters">
          <div className="controls-row">
            <button className="btn-secondary" onClick={() => setShowFilters(!showFilters)}>
              <Filter size={16} />
              Filter{' '}
              {Object.values(appliedFilters).some((f) => f) && (
                <span className="filter-badge">•</span>
              )}
            </button>
          </div>

          <div className="filter-summary">{getFilterSummary()}</div>

          {showFilters && (
            <div className="filters-dropdown">
              <div className="filters-header">
                <h3>Filters</h3>
                <button className="btn-text" onClick={clearFilters}>
                  Clear All
                </button>
              </div>

              <div className="filter-group">
                <label>Position:</label>
                <select
                  value={filters.position}
                  onChange={(e) => handleFilterChange('position', e.target.value)}
                >
                  <option value="">All Positions</option>
                  <option value="believe">Believe</option>
                  <option value="disbelieve">Disbelieve</option>
                  <option value="unsure">Unsure</option>
                </select>
              </div>

              <div className="filter-group">
                <label>User ID:</label>
                <input
                  type="number"
                  placeholder="Enter User ID"
                  value={filters.userId}
                  onChange={(e) => handleFilterChange('userId', e.target.value)}
                />
                <RecentDropdown
                  type="user"
                  dropdownKey="userId"
                  filterField="userId"
                  placeholder="Recent Users"
                />
              </div>

              <div className="filter-group filter-group-horizontal">
                <div className="filter-inputs-row">
                  <div className="filter-subgroup">
                    <label>User ID {'>'}=:</label>
                    <input
                      type="number"
                      placeholder="Min User ID"
                      value={filters.goeUserId}
                      onChange={(e) => handleFilterChange('goeUserId', e.target.value)}
                    />
                  </div>
                  <div className="filter-subgroup">
                    <label>User ID {'<'}=:</label>
                    <input
                      type="number"
                      placeholder="Max User ID"
                      value={filters.loeUserId}
                      onChange={(e) => handleFilterChange('loeUserId', e.target.value)}
                    />
                  </div>
                </div>
                <div className="quick-filter-buttons">
                  <button
                    type="button"
                    className="quick-filter-btn"
                    onClick={() => handleQuickUserFilter('11', '22')}
                  >
                    Power Users
                  </button>
                  <button
                    type="button"
                    className="quick-filter-btn"
                    onClick={() => handleQuickUserFilter('23', '144010')}
                  >
                    Dummy Users
                  </button>
                  <button
                    type="button"
                    className="quick-filter-btn"
                    onClick={() => handleQuickUserFilter('11', '144010')}
                  >
                    All AI Users
                  </button>
                </div>
              </div>

              <div className="filter-group">
                <label>On Thought:</label>
                <input
                  type="number"
                  placeholder="Enter Thought ID"
                  value={filters.thoughtId}
                  onChange={(e) => handleFilterChange('thoughtId', e.target.value)}
                />
                <RecentDropdown
                  type="thought"
                  dropdownKey="thoughtId"
                  filterField="thoughtId"
                  placeholder="Recent Thoughts"
                />
              </div>

              <div className="filter-group">
                <label>Created After:</label>
                <input
                  type="date"
                  placeholder="Enter Created After"
                  value={filters.createdAfter}
                  onChange={(e) => handleFilterChange('createdAfter', e.target.value)}
                />
              </div>

              <div className="filter-group">
                <label>Created Before:</label>
                <input
                  type="date"
                  placeholder="Enter Created Before"
                  value={filters.createdBefore}
                  onChange={(e) => handleFilterChange('createdBefore', e.target.value)}
                />
              </div>

              <div className="filter-group">
                <label>Supporting Beliefs For:</label>
                <input
                  type="number"
                  placeholder="Enter Belief ID"
                  value={filters.supportingBeliefsFor}
                  onChange={(e) => handleFilterChange('supportingBeliefsFor', e.target.value)}
                />
                <RecentDropdown
                  type="belief"
                  dropdownKey="supportingBeliefsFor"
                  filterField="supportingBeliefsFor"
                  placeholder="Recent Beliefs"
                />
              </div>

              <div className="filters-actions">
                <button className="btn-secondary" onClick={() => setShowFilters(false)}>
                  Cancel
                </button>
                <button className="btn-primary" onClick={applyFilters}>
                  Apply Filters
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      <BeliefsListView filters={listViewFilters} />
    </div>
  );
};

export default Beliefs;
